import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Class } from '../types';

interface ClassCardProps {
  classData: Class;
}

const ClassCard = ({ classData }: ClassCardProps) => {
  const navigate = useNavigate();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'submission':
        return 'FileText';
      case 'grade':
        return 'Award';
      case 'announcement':
        return 'Bell';
      default:
        return 'Activity';
    }
  };

  const getActivityLabel = (type: string) => {
    switch (type) {
      case 'submission':
        return 'Teme noi';
      case 'grade':
        return 'Note adăugate';
      case 'announcement':
        return 'Anunțuri';
      default:
        return 'Activitate';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: classData.color }}
              aria-hidden="true"
            />
            <h3 className="text-lg font-semibold text-foreground">{classData.name}</h3>
          </div>
          {classData.subject && (
            <p className="text-sm text-muted-foreground">{classData.subject}</p>
          )}
          {classData.grade && (
            <p className="text-xs text-muted-foreground mt-1">Grade {classData.grade}</p>
          )}
        </div>
        <button
          onClick={() => navigate(`/class-management-hub?class=${classData.id}`)}
          className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
          aria-label={`Vezi detaliile clasei ${classData.name}`}
        >
          <Icon name="MoreVertical" size={20} className="text-muted-foreground" />
        </button>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Elevi</span>
          <span className="font-medium text-foreground">{classData.studentCount}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Rating clasă</span>
          <span className="font-medium text-foreground flex items-center gap-2">
            <span>{typeof classData.ratingPercent === 'number' ? `${classData.ratingPercent}%` : '—'}</span>
            {typeof classData.ratingPercent === 'number' && (
              <Icon
                name={(classData.ratingTrend || 'flat') === 'up' ? 'TrendingUp' : (classData.ratingTrend || 'flat') === 'down' ? 'TrendingDown' : 'Minus'}
                size={16}
                className={
                  typeof classData.ratingDelta === 'number'
                    ? (classData.ratingTrend || 'flat') === 'up'
                      ? 'text-success'
                      : (classData.ratingTrend || 'flat') === 'down'
                        ? 'text-error'
                        : 'text-muted-foreground'
                    : 'text-muted-foreground'
                }
                title={
                  typeof classData.ratingDelta === 'number'
                    ? (classData.ratingTrend || 'flat') === 'up'
                      ? `În urcare (+${classData.ratingDelta} pp, ultimele ${classData.ratingTrendWindowDays ?? 30} zile)`
                      : (classData.ratingTrend || 'flat') === 'down'
                        ? `În coborâre (${classData.ratingDelta} pp, ultimele ${classData.ratingTrendWindowDays ?? 30} zile)`
                        : `Stagnare (0 pp, ultimele ${classData.ratingTrendWindowDays ?? 30} zile)`
                    : `Nu există date anterioare pentru comparație (ultimele ${classData.ratingTrendWindowDays ?? 30} zile).`
                }
              />
            )}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Top școală</span>
          <span className="font-medium text-foreground">
            {typeof classData.topRank === 'number' ? `Locul ${classData.topRank} (Top 5)` : (classData.topMessage || '—')}
          </span>
        </div>

        {classData.nextLesson && (
          <div className="p-3 bg-muted rounded-md">
            <div className="flex items-start gap-2">
              <Icon name="Calendar" size={16} className="text-primary mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground mb-1">Următoarea lecție</p>
                <p className="text-xs text-muted-foreground truncate">
                  {classData.nextLesson.title}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(classData.nextLesson.date).toLocaleDateString('ro-RO', {
                    month: 'short',
                    day: 'numeric',
                  })}{' '}
                  la {classData.nextLesson.time}
                </p>
              </div>
            </div>
          </div>
        )}

        {classData.recentActivity && (
          <div className="flex items-center gap-2 text-sm">
            <Icon
              name={getActivityIcon(classData.recentActivity.type)}
              size={16}
              className="text-accent"
            />
            <span className="text-muted-foreground">
              {getActivityLabel(classData.recentActivity.type)}
            </span>
            <span className="font-medium text-foreground">{classData.recentActivity.count}</span>
          </div>
        )}

        {typeof classData.progress === 'number' && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Progres</span>
              <span className="font-medium text-foreground">{classData.progress}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${classData.progress}%` }}
                role="progressbar"
                aria-valuenow={classData.progress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="BookOpen"
          iconPosition="left"
          onClick={() => navigate(`/class-management-hub?classId=${classData.id}&tab=catalog`)}
        >
          Catalog
        </Button>
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="FileText"
          iconPosition="left"
          onClick={() => navigate(`/class-management-hub?classId=${classData.id}&tab=teme`)}
        >
          Teme
        </Button>
      </div>
    </div>
  );
};

export default ClassCard;
